/**
 * TaskDetailsMobileSummary Component
 *
 * Floating pill showing critical task info on mobile.
 * Always visible at bottom of screen, tapping opens the details sheet.
 *
 * Displays:
 * - Assignee avatar and name
 * - Due date
 * - Urgency indicator
 * - Expand button
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import {
  CalendarOutlined,
  UpOutlined,
  UserOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import '../../styles/index.scss';

const TaskDetailsMobileSummary = ({
  currentTask,
  isSheetOpen,
  onToggle,
}) => {
  if (!currentTask) return null;

  const assignee = currentTask.assigneeUsers?.[0];
  const dueDate = currentTask.dueDate
    ? new Date(currentTask.dueDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  const urgency = currentTask.urgency;
  const isOverdue = currentTask.dueDate && new Date(currentTask.dueDate) < new Date();

  const getUrgencyClass = () => {
    if (!urgency) return '';
    return `task-mobile-summary__urgency--${urgency.toLowerCase()}`;
  };

  return (
    <div
      className={`task-mobile-summary ${isSheetOpen ? 'task-mobile-summary--hidden' : ''}`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-label="Open task details"
      aria-expanded={isSheetOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <div className="task-mobile-summary__content">
        {/* Assignee */}
        <div className="task-mobile-summary__assignee">
          {assignee ? (
            <>
              <AvatarS3
                imgKey={assignee.avatar?.key}
                username={assignee.name || assignee.username}
                avatarProps={{
                  size: 28,
                  className: 'task-mobile-summary__avatar',
                }}
              />
              <span className="task-mobile-summary__assignee-name">
                {startCase(assignee.name || assignee.username)}
              </span>
            </>
          ) : (
            <>
              <div className="task-mobile-summary__avatar-placeholder">
                <UserOutlined />
              </div>
              <span className="task-mobile-summary__assignee-name task-mobile-summary__assignee-name--empty">
                Unassigned
              </span>
            </>
          )}
        </div>

        {/* Due Date */}
        {dueDate && (
          <div className={`task-mobile-summary__due ${isOverdue ? 'task-mobile-summary__due--overdue' : ''}`}>
            <CalendarOutlined className="task-mobile-summary__due-icon" />
            <span>{dueDate}</span>
            {currentTask.recurring && (
              <Tooltip title="Recurring">
                <SyncOutlined className="task-mobile-summary__recurring-icon" />
              </Tooltip>
            )}
          </div>
        )}

        {/* Urgency Badge */}
        {urgency && (
          <div className={`task-mobile-summary__urgency ${getUrgencyClass()}`}>
            <span className="task-mobile-summary__urgency-dot" />
            <span className="task-mobile-summary__urgency-text">
              {startCase(urgency.toLowerCase())}
            </span>
          </div>
        )}
      </div>

      {/* Expand Button */}
      <button
        className="task-mobile-summary__expand"
        aria-label="Expand details"
        type="button"
      >
        <UpOutlined className="task-mobile-summary__expand-icon" />
      </button>
    </div>
  );
};

TaskDetailsMobileSummary.propTypes = {
  currentTask: PropTypes.shape({
    assigneeUsers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        username: PropTypes.string,
        avatar: PropTypes.shape({
          key: PropTypes.string,
        }),
      })
    ),
    dueDate: PropTypes.string,
    urgency: PropTypes.oneOf(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
    recurring: PropTypes.bool,
  }),
  isSheetOpen: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

TaskDetailsMobileSummary.defaultProps = {
  currentTask: null,
  isSheetOpen: false,
};

export default TaskDetailsMobileSummary;
